// MarketWorkbenchTable — preview-only "strategic signal workbench" table
// with sortable columns.
//
// Sorting model
//   • Flat variant: clicking a header sorts every row by that column.
//   • Grouped variant: parent rows ALWAYS stay at index 0 of their
//     group (hierarchy invariant). Group ordering uses the parent's
//     value for the selected column; children sort within their group
//     using the same comparator. Header clicks never move a child
//     above its parent.

import React, { useMemo } from "react";
import {
    externalRoleLabel,
    marketZoneLabel,
    evidenceLabel,
    thisWeekMovementLabel,
    delta4w,
    formatDelta,
    deltaTone,
    lastSeenYearWeek,
    topCompaniesLabel,
    groupByParent,
    groupByZone,
} from "../../lib/workbench/labels";
import SortHeader from "./SortHeader";

const Badge = ({ tone = "muted", children }) => (
    <span className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

/** Pull the comparator value for a given sort key off a track + its
 *  pre-derived display fields. Returns a sortable primitive (number,
 *  string, or null — null sinks to the bottom regardless of direction). */
const valueFor = (key, track, derived) => {
    switch (key) {
        case "name": return track.StableTrackName || "";
        case "role": return derived.role.label || "";
        case "zone": return derived.zone === "—" ? "" : derived.zone;
        case "thisWeek": return derived.movement || "";
        case "score": return typeof track.LatestScore === "number" ? track.LatestScore : null;
        case "delta4w": return typeof derived.d4w === "number" ? derived.d4w : null;
        case "evidence": return derived.evidence === "—" ? "" : derived.evidence;
        case "companies": return derived.companies || "";
        case "lastSeen": return derived.lastSeen || "";
        default: return null;
    }
};

/** A stable comparator factory: nulls sink, strings compare locale-aware,
 *  numbers compare numerically, direction flips the sign. */
const makeCompare = (sortKey, sortDir) => (a, b) => {
    const av = a.__sortValue;
    const bv = b.__sortValue;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    let cmp;
    if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
    else cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
};

const buildDerived = (track, history) => {
    const role = externalRoleLabel(track);
    const zone = marketZoneLabel(track);
    const evidence = evidenceLabel(track);
    const movement = thisWeekMovementLabel(track) || "No current movement";
    const d4w = delta4w(history);
    const lastSeen = lastSeenYearWeek(track, history);
    const companies = topCompaniesLabel(track, 3, history);
    const lifecycle = track.LifecycleStatus ? track.LifecycleStatus.toLowerCase() : null;
    return { role, zone, evidence, movement, d4w, lastSeen, companies, lifecycle };
};

const Row = ({ track, derived, selected, onSelect, isChild }) => (
    <tr
        data-testid={`workbench-row-${track.TrackID}`}
        onClick={() => onSelect(track.TrackID)}
        className={`cursor-pointer transition-colors ${selected ? "bg-[hsl(var(--accent-soft))]" : "hover:bg-[hsl(var(--surface-sunken))]"}`}
    >
        <td className={`align-top py-2.5 ${isChild ? "pl-8" : "pl-3"} pr-3 min-w-[260px] max-w-[360px]`}>
            <div className="text-sm font-medium text-[hsl(var(--ink))] leading-snug">{track.StableTrackName}</div>
            <div className="text-[11px] text-[hsl(var(--mute))] mt-0.5 leading-snug">
                <span>{derived.role.label}</span>
                {derived.zone !== "—" && <><span> · </span><span>{derived.zone}</span></>}
                {derived.lifecycle && <><span> · </span><span>{derived.lifecycle}</span></>}
                {derived.evidence !== "—" && <><span> · </span><span>{derived.evidence}</span></>}
            </div>
        </td>
        <td className="align-top py-2.5 px-3 whitespace-nowrap"><Badge tone={derived.role.tone}>{derived.role.label}</Badge></td>
        <td className="align-top py-2.5 px-3 text-[12px] text-[hsl(var(--ink-soft))] leading-snug max-w-[200px]">{derived.zone === "—" ? <span className="text-[hsl(var(--mute))]">—</span> : derived.zone}</td>
        <td className="align-top py-2.5 px-3 text-[12px] text-[hsl(var(--ink-soft))] whitespace-nowrap">{derived.movement}</td>
        <td className="align-top py-2.5 px-3 text-sm num text-[hsl(var(--ink))] whitespace-nowrap">{typeof track.LatestScore === "number" ? track.LatestScore : "—"}</td>
        <td className="align-top py-2.5 px-3 num whitespace-nowrap"><Badge tone={deltaTone(derived.d4w)}>{formatDelta(derived.d4w)}</Badge></td>
        <td className="align-top py-2.5 px-3 text-[12px] text-[hsl(var(--ink-soft))] max-w-[180px]"><div className="leading-snug">{derived.evidence === "—" ? <span className="text-[hsl(var(--mute))]">—</span> : derived.evidence}</div></td>
        <td className="align-top py-2.5 px-3 text-[12px] text-[hsl(var(--ink-soft))] leading-snug max-w-[260px]">{derived.companies || <span className="text-[hsl(var(--mute))]">—</span>}</td>
        <td className="align-top py-2.5 pr-3 pl-3 text-[12px] text-[hsl(var(--ink-soft))] num whitespace-nowrap">{derived.lastSeen || "—"}</td>
    </tr>
);

const Header = ({ sortBy, sortDir, onSort }) => (
    <thead>
        <tr className="border-b border-[hsl(var(--line))]">
            <SortHeader label="Signal"      sortKey="name"      currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="pl-3 pr-3" />
            <SortHeader label="Signal role" sortKey="role"      currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Market zone" sortKey="zone"      currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="This week"   sortKey="thisWeek"  currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Score"       sortKey="score"     currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="desc" className="px-3" />
            <SortHeader label="Δ4w"         sortKey="delta4w"   currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="desc" className="px-3" />
            <SortHeader label="Evidence"    sortKey="evidence"  currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Companies"   sortKey="companies" currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="asc"  className="px-3" />
            <SortHeader label="Last seen"   sortKey="lastSeen"  currentKey={sortBy} currentDir={sortDir} onSort={onSort} initialDir="desc" className="pl-3 pr-3" />
        </tr>
    </thead>
);

const FlatTable = ({ tracks, historyByTrackId, selectedTrackId, onSelectTrack, sortBy, sortDir, onSort }) => {
    const enriched = useMemo(() => tracks.map((t) => {
        const d = buildDerived(t, historyByTrackId?.[t.TrackID]);
        return { track: t, derived: d, __sortValue: valueFor(sortBy, t, d) };
    }), [tracks, historyByTrackId, sortBy]);
    const sorted = useMemo(() => enriched.slice().sort(makeCompare(sortBy, sortDir)), [enriched, sortBy, sortDir]);
    return (
        <div className="card-flat overflow-x-auto" data-testid="workbench-market-flat">
            <table className="w-full text-sm">
                <Header sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                <tbody>
                    {sorted.map(({ track, derived }) => (
                        <Row key={track.TrackID} track={track} derived={derived} selected={selectedTrackId === track.TrackID} onSelect={onSelectTrack} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const GroupedTable = ({ tracks, historyByTrackId, selectedTrackId, onSelectTrack, groupBy, sortBy, sortDir, onSort }) => {
    const groups = useMemo(() => (groupBy === "zone" ? groupByZone(tracks) : groupByParent(tracks)), [tracks, groupBy]);

    // Pre-derive every row's display values + sort value once.
    const enrichedGroups = useMemo(() => {
        return groups.map((g) => {
            const enrichedChildren = g.children.map((t) => {
                const d = buildDerived(t, historyByTrackId?.[t.TrackID]);
                return { track: t, derived: d, __sortValue: valueFor(sortBy, t, d) };
            });
            // Parent's sort value drives the group order. In zone-mode use
            // the zone-key itself; in parent-mode use the actual parent row.
            const parentTrack = g.parent;
            const parentDerived = parentTrack ? buildDerived(parentTrack, historyByTrackId?.[parentTrack.TrackID]) : null;
            const groupSortValue = groupBy === "zone"
                ? g.key
                : (parentTrack ? valueFor(sortBy, parentTrack, parentDerived) : null);
            return { ...g, enrichedChildren, parentDerived, __sortValue: groupSortValue };
        });
    }, [groups, historyByTrackId, sortBy, groupBy]);

    // Sort groups by parent attribute.
    const sortedGroups = useMemo(() => enrichedGroups.slice().sort(makeCompare(sortBy, sortDir)), [enrichedGroups, sortBy, sortDir]);

    // Sort children WITHIN each group — but in parent-mode keep the parent row
    // (children[0]) pinned at index 0 regardless of its sort value.
    const finalGroups = useMemo(() => sortedGroups.map((g) => {
        if (groupBy === "parent" && g.parent) {
            const [parent, ...rest] = g.enrichedChildren;
            const sortedRest = rest.slice().sort(makeCompare(sortBy, sortDir));
            return { ...g, ordered: [parent, ...sortedRest] };
        }
        return { ...g, ordered: g.enrichedChildren.slice().sort(makeCompare(sortBy, sortDir)) };
    }), [sortedGroups, sortBy, sortDir, groupBy]);

    return (
        <div className="card-flat overflow-x-auto" data-testid="workbench-market-grouped">
            <table className="w-full text-sm">
                <Header sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
                <tbody>
                    {finalGroups.map((g) => {
                        const heading = groupBy === "zone"
                            ? `Market zone · ${g.key}`
                            : g.parent
                                ? `Main signal · ${g.parent.StableTrackName}`
                                : "Unparented signals";
                        return (
                            <React.Fragment key={g.key}>
                                <tr className="bg-[hsl(var(--surface-sunken))] border-t border-[hsl(var(--line))]" data-testid={`workbench-group-${g.key}`}>
                                    <td colSpan={9} className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-[hsl(var(--ink-soft))] font-medium">
                                        {heading}
                                        <span className="ml-2 text-[hsl(var(--mute))] normal-case tracking-normal">· {g.ordered.length}</span>
                                    </td>
                                </tr>
                                {g.ordered.map(({ track, derived }, idx) => (
                                    <Row
                                        key={track.TrackID}
                                        track={track}
                                        derived={derived}
                                        selected={selectedTrackId === track.TrackID}
                                        onSelect={onSelectTrack}
                                        isChild={groupBy === "parent" && g.parent && idx > 0}
                                    />
                                ))}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const MarketWorkbenchTable = ({ variant = "flat", tracks, historyByTrackId, selectedTrackId, onSelectTrack, groupBy = "parent", sortBy = "score", sortDir = "desc", onSort }) => {
    if (!Array.isArray(tracks) || tracks.length === 0) {
        return <div className="card-flat p-6 text-sm text-[hsl(var(--mute))]">No signals to display.</div>;
    }
    return variant === "grouped"
        ? <GroupedTable tracks={tracks} historyByTrackId={historyByTrackId} selectedTrackId={selectedTrackId} onSelectTrack={onSelectTrack} groupBy={groupBy} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
        : <FlatTable tracks={tracks} historyByTrackId={historyByTrackId} selectedTrackId={selectedTrackId} onSelectTrack={onSelectTrack} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />;
};

export default MarketWorkbenchTable;
